'use client'

import { useRef } from 'react'
import {
  CameraIcon,
  ArrowUpIcon,
  MicrophoneIcon,
  StopCircleIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react'
import { AnimatePresence, motion } from 'motion/react'
import { Button } from '@/ui/button'
import { cn } from '@/lib/tailwind'

interface CaptureActionBarProps {
  hasContent: boolean
  isRecording: boolean
  isConnecting: boolean
  isSubmitting: boolean
  disabled: boolean
  onAttach: () => void
  onSend: () => void
  onStartRecording: () => void
  onStopRecording: () => void
  className?: string
}

type SendButtonState =
  | 'submitting'
  | 'connecting'
  | 'recording'
  | 'send'
  | 'mic'

function getSendButtonState(
  isSubmitting: boolean,
  isConnecting: boolean,
  isRecording: boolean,
  hasContent: boolean,
): SendButtonState {
  if (isSubmitting) return 'submitting'
  if (isConnecting) return 'connecting'
  if (isRecording) return 'recording'
  if (hasContent) return 'send'
  return 'mic'
}

interface MorphingSendButtonProps {
  state: SendButtonState
  disabled: boolean
  onSend: () => void
  onStartRecording: () => void
  onStopRecording: () => void
}

function MorphingSendButton({
  state,
  disabled,
  onSend,
  onStartRecording,
  onStopRecording,
}: MorphingSendButtonProps) {
  function handleClick() {
    if (state === 'recording') {
      onStopRecording()
    } else if (state === 'send') {
      onSend()
    } else if (state === 'mic') {
      onStartRecording()
    }
  }

  const isLoading = state === 'submitting' || state === 'connecting'
  const isDisabled = disabled || isLoading

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={cn(
        'relative flex h-8 w-8 items-center justify-center rounded-full transition-colors',
        state === 'send'
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:text-foreground',
        isDisabled && 'cursor-not-allowed opacity-50',
      )}
      aria-label={
        state === 'recording'
          ? 'Stop recording'
          : state === 'send'
            ? 'Send'
            : state === 'mic'
              ? 'Start recording'
              : 'Loading'
      }
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={state}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.15 }}
          className="flex items-center justify-center"
        >
          {(state === 'submitting' || state === 'connecting') && (
            <CircleNotchIcon className="h-4 w-4 animate-spin" weight="bold" />
          )}
          {state === 'recording' && (
            <StopCircleIcon className="h-4 w-4 text-red-500" weight="fill" />
          )}
          {state === 'send' && (
            <ArrowUpIcon className="h-4 w-4" weight="bold" />
          )}
          {state === 'mic' && (
            <MicrophoneIcon className="h-4 w-4" weight="regular" />
          )}
        </motion.div>
      </AnimatePresence>
    </button>
  )
}

export function CaptureActionBar({
  hasContent,
  isRecording,
  isConnecting,
  isSubmitting,
  disabled,
  onAttach,
  onSend,
  onStartRecording,
  onStopRecording,
  className,
}: CaptureActionBarProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleAttachClick() {
    fileInputRef.current?.click()
    onAttach()
  }

  const sendState = getSendButtonState(
    isSubmitting,
    isConnecting,
    isRecording,
    hasContent,
  )

  return (
    <div
      className={cn('flex items-center justify-between p-1.5 pt-0', className)}
    >
      {/* Left side — attach */}
      <div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleAttachClick}
          disabled={disabled}
          className="text-muted-foreground hover:text-foreground h-8 w-8 rounded-full"
          aria-label="Attach image"
        >
          <CameraIcon className="h-4 w-4" weight="regular" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      {/* Right side — morphing send button */}
      <MorphingSendButton
        state={sendState}
        disabled={disabled}
        onSend={onSend}
        onStartRecording={onStartRecording}
        onStopRecording={onStopRecording}
      />
    </div>
  )
}
